// Permit extension (0020): the PA or PH asks the Responsible Supervisor to extend the
// authorised period; the RS approves or refuses; the requester may withdraw. No Operations
// Manager step: the OM approval of the authorisation being extended carries over. The panels
// reuse the daily-cycle panel frame and command hook, so the signer sends the row version
// they were shown and acknowledges any change before signing. The server decides; the form
// pre-checks the new end against the allowance so problems show before sending.
import { useId, useState } from 'react'
import { SignatureLine } from '@/components/evidence'
import { Badge, Banner, Card, Field, KeyValue, SectionTitle, TextArea } from '@/components/ui'
import {
  durationText,
  EXTENSION_APPROVAL_DECLARATION,
  EXTENSION_REFUSAL_DECLARATION,
  EXTENSION_REQUEST_DECLARATION,
  EXTENSION_STATUS_TEXT,
  EXTENSION_WITHDRAWAL_DECLARATION,
  extensionRequests,
  extensionSummary,
  openExtensionRequest,
  proposedEndProblems,
} from '@/domain/extension'
import type { ExtensionRequestView, PermitBundle } from '@/domain/types'
import type { ActionPanelProps } from '@/features/contracts'
import { PanelFrame } from '@/features/cycles/frame'
import { usePanelClock, usePanelCommand } from '@/features/cycles/hooks'
import { actionArg } from '@/features/cycles/logic'
import { DateTimeField, roleLabel, SigningStatement, TickRow } from '@/features/cycles/parts'
import { fmtDateTime, inputTimeArg, toLocalInput } from '@/lib/format'

const DAY_MS = 24 * 3_600_000

/** The request an action names (args.extension_request_id), else the open one. */
function requestFor(bundle: PermitBundle, action: ActionPanelProps['action']): ExtensionRequestView | null {
  const id = actionArg(action, 'extension_request_id')
  return extensionRequests(bundle).find((r) => r.id === id) ?? openExtensionRequest(bundle)
}

function AllowanceFacts({ bundle }: { bundle: PermitBundle }) {
  const s = extensionSummary(bundle)
  if (!s) return <p className="text-sm text-ink-soft">This permit has no current authorisation.</p>
  return (
    <KeyValue
      items={[
        ['Original authorised end', fmtDateTime(s.originalTo)],
        ['Current authorised end', `${fmtDateTime(s.currentTo)}${s.extended ? ' (extended)' : ''}`],
        ['Extension allowance', s.allowanceDays === null ? 'Not usable in these rules' : `${s.allowanceDays} days in total after the original end`],
        ['Latest possible end', s.latestAllowedTo ? fmtDateTime(s.latestAllowedTo) : '-'],
        ['Allowance used', durationText(s.usedMs)],
        ['Allowance left', s.latestAllowedTo ? durationText(s.remainingMs) : '-'],
      ]}
    />
  )
}

function RequestFacts({ r }: { r: ExtensionRequestView }) {
  return (
    <KeyValue
      items={[
        ['Asked by', `${r.requested.signer.display_name} (${roleLabel(r.requester_role)}), ${fmtDateTime(r.requested.server_signed_at)}`],
        ['Authorised end when asked', fmtDateTime(r.previous_authorisation_to)],
        ['New end asked for', fmtDateTime(r.requested_to)],
        ['Extra time', durationText(new Date(r.requested_to).getTime() - new Date(r.previous_authorisation_to).getTime())],
        ['Reason', r.reason],
      ]}
    />
  )
}

// -----------------------------------------------------------------------------
// request_extension (PA or PH of the permit)
// -----------------------------------------------------------------------------
export function RequestExtensionPanel({ bundle, action, onDone, onCancel }: ActionPanelProps) {
  const now = usePanelClock(bundle)
  const run = usePanelCommand('request_extension', bundle, onDone)
  const summary = extensionSummary(bundle)
  const reasonId = useId()
  // Default: one more day, never past the latest possible end.
  const [initialEnd] = useState(() => {
    if (!summary) return null
    const current = new Date(summary.currentTo).getTime()
    const latest = summary.latestAllowedTo ? new Date(summary.latestAllowedTo).getTime() : current + DAY_MS
    return new Date(Math.min(current + DAY_MS, Math.max(latest, current))).toISOString()
  })
  const [end, setEnd] = useState(() => toLocalInput(initialEnd))
  const [reason, setReason] = useState('')
  const [confirmed, setConfirmed] = useState(false)
  const [tried, setTried] = useState(false)

  const endIso = inputTimeArg(end, initialEnd)
  const endProblems = proposedEndProblems(summary, endIso, now(), fmtDateTime)
  const problems = [...endProblems]
  if (!reason.trim()) problems.push('Give the reason the work needs more time.')
  if (!confirmed) problems.push('Tick the declaration to send the request.')

  function submit() {
    setTried(true)
    if (problems.length > 0 || !endIso) return
    void run.submit({ permit_id: bundle.permit.id, requested_to: endIso, reason: reason.trim() })
  }

  return (
    <PanelFrame action={action} run={run} problems={problems} tried={tried} onCancel={onCancel} onSubmit={submit} submitLabel="Send the request">
      <p className="mb-4 text-sm text-ink">
        Ask the Responsible Supervisor to extend the authorised period of permit <span className="code">{bundle.permit.permit_number}</span>.
        Ask before the current authorised end: after it the permit cannot be extended and the work needs a new permit. The scope,
        location, hazards and controls stay as authorised; a change of scope needs a new permit instead.
      </p>
      <section className="mb-4 rounded-lg border border-line bg-white p-3" aria-label="Authorised period and allowance">
        <AllowanceFacts bundle={bundle} />
        <p className="mt-2 text-xs text-ink-soft">
          Extensions may add at most the allowance in total after the original end, over one or more requests. The RS decides; no
          Operations Manager approval is needed again.
        </p>
      </section>

      <DateTimeField
        label="New authorised end"
        required
        value={end}
        onChange={setEnd}
        hint={summary?.latestAllowedTo ? `No later than ${fmtDateTime(summary.latestAllowedTo)}.` : undefined}
      />
      {tried || end !== toLocalInput(initialEnd)
        ? endProblems.length > 0
          ? (
            <Banner tone="warn" title="Check the new end" className="mb-4">
              <ul className="list-disc space-y-1 pl-5">
                {endProblems.map((p) => (
                  <li key={p}>{p}</li>
                ))}
              </ul>
            </Banner>
          )
          : null
        : null}

      <Field htmlFor={reasonId} label="Why the work needs more time" required hint="Saved with your signed request; the RS reads it before deciding.">
        <TextArea id={reasonId} rows={3} value={reason} onChange={(e) => setReason(e.target.value)} />
      </Field>

      <SigningStatement role={action.role} person={bundle.viewer.person}>
        <p>{EXTENSION_REQUEST_DECLARATION}</p>
      </SigningStatement>
      <div className="mb-4 rounded-lg border border-line bg-white">
        <TickRow checked={confirmed} onChange={setConfirmed} label="I confirm the declaration above and ask for this extension." />
      </div>
    </PanelFrame>
  )
}

// -----------------------------------------------------------------------------
// approve_extension / refuse_extension (RS)
// -----------------------------------------------------------------------------
export function DecideExtensionPanel({ bundle, action, onDone, onCancel }: ActionPanelProps) {
  const approving = action.command === 'approve_extension'
  const run = usePanelCommand(approving ? 'approve_extension' : 'refuse_extension', bundle, onDone)
  const r = requestFor(bundle, action)
  const [text, setText] = useState('')
  const [confirmed, setConfirmed] = useState(false)
  const [tried, setTried] = useState(false)
  const textId = useId()

  const problems: string[] = []
  if (!approving && !text.trim()) problems.push('Give the reason you refuse the extension.')
  if (approving && !confirmed) problems.push('Tick the declaration to approve the extension.')

  function submit() {
    setTried(true)
    if (!r || problems.length > 0) return
    if (approving) {
      void run.submit({ permit_id: bundle.permit.id, extension_request_id: r.id, declaration_confirmed: true, notes: text.trim() ? text.trim() : null })
    } else {
      void run.submit({ permit_id: bundle.permit.id, extension_request_id: r.id, reason: text.trim() })
    }
  }

  return (
    <PanelFrame
      action={action}
      run={run}
      problems={problems}
      tried={tried}
      onCancel={onCancel}
      onSubmit={submit}
      submitLabel={approving ? 'Sign the extension' : 'Sign the refusal'}
      danger={!approving}
      blockedNote={r ? null : 'The extension request was not found. Refresh the permit.'}
    >
      <section className="mb-4 rounded-lg border border-line bg-white p-3" aria-label="The request">
        <p className="mb-2 text-sm font-semibold text-ink">The request</p>
        {r ? <RequestFacts r={r} /> : null}
      </section>
      <section className="mb-4 rounded-lg border border-line bg-white p-3" aria-label="Authorised period and allowance">
        <AllowanceFacts bundle={bundle} />
      </section>
      {approving ? (
        <>
          <p className="mb-4 text-xs text-ink-soft">
            Check the work, its scope and its controls are unchanged before you approve. You must not be the requester, the PA or the
            PH of this permit; the server checks that and your RS licence for this unit. The Operations Manager approval carries
            over: no new OM approval is needed.
          </p>
          <SigningStatement role="RS" person={bundle.viewer.person}>
            <p>{EXTENSION_APPROVAL_DECLARATION}</p>
          </SigningStatement>
          <div className="mb-4 rounded-lg border border-line bg-white">
            <TickRow checked={confirmed} onChange={setConfirmed} label="I confirm the declaration above and approve this extension." />
          </div>
          <Field htmlFor={textId} label="Notes (optional)" hint="Anything you checked or want the PA and PH to know. Saved with your approval.">
            <TextArea id={textId} rows={3} value={text} onChange={(e) => setText(e.target.value)} />
          </Field>
        </>
      ) : (
        <>
          <SigningStatement role="RS" person={bundle.viewer.person}>
            <p>{EXTENSION_REFUSAL_DECLARATION}</p>
          </SigningStatement>
          <Field htmlFor={textId} label="Why you refuse the extension" required hint="Saved with your signed refusal; the requester sees it.">
            <TextArea id={textId} rows={3} value={text} onChange={(e) => setText(e.target.value)} />
          </Field>
        </>
      )}
    </PanelFrame>
  )
}

// -----------------------------------------------------------------------------
// withdraw_extension (the requester)
// -----------------------------------------------------------------------------
export function WithdrawExtensionPanel({ bundle, action, onDone, onCancel }: ActionPanelProps) {
  const run = usePanelCommand('withdraw_extension', bundle, onDone)
  const r = requestFor(bundle, action)
  const [reason, setReason] = useState('')
  const reasonId = useId()

  function submit() {
    if (!r) return
    void run.submit({ permit_id: bundle.permit.id, extension_request_id: r.id, reason: reason.trim() ? reason.trim() : null })
  }

  return (
    <PanelFrame
      action={action}
      run={run}
      problems={[]}
      tried={false}
      onCancel={onCancel}
      onSubmit={submit}
      submitLabel="Withdraw the request"
      danger
      blockedNote={r ? null : 'The extension request was not found. Refresh the permit.'}
    >
      <section className="mb-4 rounded-lg border border-line bg-white p-3" aria-label="Your request">
        {r ? <RequestFacts r={r} /> : null}
      </section>
      <SigningStatement role={action.role} person={bundle.viewer.person}>
        <p>{EXTENSION_WITHDRAWAL_DECLARATION}</p>
      </SigningStatement>
      <Field htmlFor={reasonId} label="Why you withdraw it (optional)">
        <TextArea id={reasonId} rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
      </Field>
    </PanelFrame>
  )
}

// -----------------------------------------------------------------------------
// Status on the permit page
// -----------------------------------------------------------------------------
export function ExtensionCard({ bundle }: { bundle: PermitBundle }) {
  const s = extensionSummary(bundle)
  const list = extensionRequests(bundle)
  if (!s || (!s.extended && list.length === 0)) return null
  const tone = s.open ? 'warn' : s.extended ? 'ok' : 'neutral'
  const label = s.open ? 'Extension requested' : s.extended ? 'Extended' : 'Not extended'
  return (
    <Card>
      <SectionTitle aside={<Badge tone={tone}>{label}</Badge>}>Authorised period and extensions</SectionTitle>
      <AllowanceFacts bundle={bundle} />
      <ul className="mt-3 space-y-2">
        {list.map((r) => {
          const st = EXTENSION_STATUS_TEXT[r.status]
          return (
            <li key={r.id} className="rounded-lg border border-line bg-white p-3">
              <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
                <p className="text-sm font-semibold text-ink">To {fmtDateTime(r.requested_to)}</p>
                <Badge tone={st.tone}>{st.label}</Badge>
              </div>
              <p className="text-sm text-ink-soft">Reason: {r.reason}</p>
              <div className="mt-2 space-y-1">
                <SignatureLine a={r.requested} label={`Asked by the ${roleLabel(r.requester_role)}`} />
                {r.decision ? (
                  <>
                    <SignatureLine
                      a={r.decision.attestation}
                      label={r.decision.decision === 'approved' ? 'Approved by the RS' : r.decision.decision === 'refused' ? 'Refused by the RS' : 'Withdrawn by the requester'}
                    />
                    {r.decision.reason ? <p className="text-sm text-ink-soft">{r.decision.decision === 'refused' ? 'Why refused' : 'Why withdrawn'}: {r.decision.reason}</p> : null}
                    {r.decision.notes ? <p className="text-sm text-ink-soft">RS notes: {r.decision.notes}</p> : null}
                  </>
                ) : null}
              </div>
            </li>
          )
        })}
      </ul>
    </Card>
  )
}
